import {
    type FC,
    type MouseEventHandler,
    type ReactElement,
    type ComponentProps,
    useCallback,
    useRef,
} from 'react';
import { useFragment, graphql, type LoadMoreFn } from 'react-relay';
import { type OperationType } from 'relay-runtime';
import classnames from 'classnames';

import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { SeoLink } from 'dibs-elements/exports/SeoLink.tsx';
import ItemTilesCarousel from 'dibs-product-tile-carousel/exports/ItemTilesCarousel.ts';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';

import { viewAll } from '../../pdpMessages.tsx';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { FavoritesItem } from '../../../components/global/FavoriteIcons/FavoritesItem.tsx';
import { FavoritesProvider } from '../../../components/global/FavoriteIcons/FavoritesProvider.tsx';

import { type PdpSharedProductTileCarousel_viewer$key } from './__generated__/PdpSharedProductTileCarousel_viewer.graphql.ts';
import { type PdpSharedProductTileCarousel_items$key } from './__generated__/PdpSharedProductTileCarousel_items.graphql.ts';
import { type PdpSharedProductTileCarousel_user$key } from './__generated__/PdpSharedProductTileCarousel_user.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './PdpSharedProductTileCarousel.scss';

const viewerFragment = graphql`
    fragment PdpSharedProductTileCarousel_viewer on Viewer
    @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
        ...ItemTilesCarousel_viewer @arguments(fetchFavorites: false, userId: $userId)
    }
`;
const itemsFragment = graphql`
    fragment PdpSharedProductTileCarousel_items on Item
    @relay(plural: true)
    @argumentDefinitions(
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        priceBookName: { type: "String" }
    ) {
        serviceId
        ...ItemTilesCarousel_items
            @arguments(
                fetchFavorites: false
                userId: $userId
                isTrade: $isTrade
                priceBookName: $priceBookName
            )
        ...FavoritesItem_item
        ...FavoritesProvider_item
    }
`;

const userFragment = graphql`
    fragment PdpSharedProductTileCarousel_user on User {
        serviceId
        ...ItemTilesCarousel_user
    }
`;

const CAROUSEL_ITEMS_TO_SHOW_RESP = 5;
const CAROUSEL_ITEMS_TO_SHOW_MOBILE = 1.5;

export const PdpSharedProductTileCarousel: FC<{
    viewer: PdpSharedProductTileCarousel_viewer$key;
    items: PdpSharedProductTileCarousel_items$key;
    user: PdpSharedProductTileCarousel_user$key | null | undefined;
    title: ReactElement;
    viewAllLink?: string;
    isViewAllLinkable?: boolean;
    viewAllComponent?: ReactElement;
    handleViewAllClick?: MouseEventHandler;
    currency: string;
    trackingList: string;
    dataTn?: string;
    carouselCapacity?: number;
    totalResults: number;
    loadNext?: LoadMoreFn<OperationType>;
    onCarouselVisible?: () => void;
    classOverrides?: {
        container?: string;
        title?: string;
        viewAllLink?: string;
    };
    onArrowClick?: ComponentProps<typeof ItemTilesCarousel>['onArrowClick'];
    isSponsored?: boolean;
    handleOnItemClick?: (itemId: string | null | undefined) => void;
    handleOnItemVisible?: (itemId: string | null | undefined) => void;
    showArrows?: boolean;
}> = ({
    viewer: viewerRef,
    items: itemsRef,
    user: userRef,
    title,
    viewAllLink,
    isViewAllLinkable = true,
    viewAllComponent = viewAll,
    handleViewAllClick = () => {},
    currency,
    trackingList,
    dataTn,
    carouselCapacity = 10,
    totalResults,
    loadNext,
    onCarouselVisible = () => {},
    classOverrides = {},
    onArrowClick,
    isSponsored = false,
    handleOnItemClick,
    handleOnItemVisible,
    showArrows,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const items = useFragment(itemsFragment, itemsRef);
    const user = useFragment(userFragment, userRef);

    const wrapperRef = useRef<HTMLDivElement>(null);

    const { isMobile } = useServerVarsContext();
    const isClient = useClientState();

    const onVisibilityChange: ComponentProps<typeof VisibilityTracker>['onVisibilityChange'] =
        useCallback(
            ({ isVisible }) => {
                if (isVisible) {
                    onCarouselVisible();
                }
            },
            [onCarouselVisible]
        );

    if (!items.length) {
        return null;
    }

    return (
        <div
            ref={wrapperRef}
            className={
                classOverrides.container ||
                classnames({
                    [styles.container]: isMobile,
                    [dibsCss.pSmall]: isMobile,
                    [dibsCss.mbMedium]: !isMobile,
                })
            }
            data-tn={dataTn}
        >
            <VisibilityTracker
                elementRef={wrapperRef}
                onVisibilityChange={onVisibilityChange}
                observerOptions={{ threshold: 0.5 }}
            />
            <div className={dibsCss.mbSmall}>
                {title && (
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={
                                    classOverrides.title ||
                                    classnames({
                                        [dibsCss.sassyFontHeaderMedium]: isMobile,
                                        [dibsCss.sassyFontHeaderLarge]: !isMobile,
                                        [dibsCss.m0]: !isMobile || viewAllLink,
                                        [dibsCss.mbSmall]: isMobile && !viewAllLink,
                                        [dibsCss.mt0]: isMobile && !viewAllLink,
                                    })
                                }
                            >
                                {title}
                            </Heading>
                        )}
                    </HeadingLevel>
                )}
                {viewAllLink && (
                    <SeoLink
                        className={
                            classOverrides.viewAllLink ||
                            classnames(dibsCss.sassyFontBody, dibsCss.block, {
                                [dibsCss.mbSmall]: isMobile,
                                [dibsCss.mtXsmall]: !isMobile,
                            })
                        }
                        onClick={handleViewAllClick}
                        linkData={{
                            path: viewAllLink,
                            isLinkable: isViewAllLinkable,
                        }}
                        dataTn={`${dataTn || 'view-all'}-link`}
                    >
                        {viewAllComponent}
                    </SeoLink>
                )}
            </div>

            <FavoritesProvider
                userId={user?.serviceId}
                items={items}
                location={trackingList as $TSFixMe}
                disable={!isClient}
                fetchFolder={!isMobile}
            >
                {({ props: favorites }) => (
                    <ItemTilesCarousel
                        viewer={viewer}
                        user={user}
                        items={items}
                        currency={currency}
                        authModalLoader={authModalLoader}
                        carouselCapacity={carouselCapacity}
                        imageBackgroundColor="moonStone"
                        totalResults={totalResults}
                        loadNext={loadNext}
                        trackingList={trackingList}
                        itemsToShow={
                            isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP
                        }
                        tileSize={isMobile ? 'small' : 'medium'}
                        isMobile={isMobile || false}
                        isInfinite={!isMobile}
                        showArrows={showArrows === undefined ? !isMobile : showArrows}
                        showQuickView={!isMobile}
                        gapSize="small"
                        fullWidth={!!isMobile}
                        onArrowClick={onArrowClick}
                        favoritesComponentOverrides={items.map(
                            item =>
                                ({ isFolderVisible, ...favoritesItemProps }) => (
                                    <FavoritesItem
                                        wrapperClass={classnames(
                                            dibsCss.zBump,
                                            dibsCss.absolute,
                                            dibsCss.topXxsmall,
                                            dibsCss.rightXxsmall
                                        )}
                                        folderIconWrapperClass={dibsCss.mtXxsmall}
                                        showFolderPlusIcon
                                        item={item}
                                        {...favorites}
                                        {...favoritesItemProps}
                                        fetchFolder={!!isFolderVisible && !isMobile}
                                        theme={
                                            isMobile
                                                ? 'circle-frosted-small'
                                                : 'circle-frosted-medium'
                                        }
                                    />
                                )
                        )}
                        isSponsored={isSponsored}
                        onClick={handleOnItemClick}
                        onContentVisible={handleOnItemVisible}
                        dataTn={dataTn}
                    />
                )}
            </FavoritesProvider>
        </div>
    );
};
